import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'next-i18next';

import { types } from '@boss/boss-client';
import type { DeliveryMethod, StockStatusSummary } from '@boss/services/client';

import type { ShopSelection } from '../../hooks/use-basket-stock/use-basket-stock';
import { isB2b } from '../../utils';
import PickupPointDropdown from '../PickupPointDropdown';

type Props = {
  deliveryMethod: DeliveryMethod;
  favouriteShopId: string | undefined;
  onDeliveryMethodChange: (method: DeliveryMethod) => void;
  onShopChange: (shop: ShopSelection) => void;
  selectedShop: ShopSelection | undefined;
  stores: types.Store[];
  storeStockMap: Map<string, 'green' | 'red'>;
  methodSummaries: { Delivery: StockStatusSummary; Pickup: StockStatusSummary };
};

// --- Module-level pure helpers (extracted to reduce cognitive complexity of the component) ---

const statusColorClasses = {
  green: 'bg-success',
  red: 'bg-red',
} as const;

/** Green/red dot for a delivery method, with text when no visible status copy accompanies it. */
const summaryDot = (color: 'green' | 'red', large: boolean, accessibleLabel?: string) => (
  <span className={`relative inline-flex shrink-0 ${large ? 'h-2.5 w-2.5' : 'ml-1 h-2 w-2'}`}>
    <span aria-hidden="true" className={`h-full w-full rounded-full ${statusColorClasses[color]}`} />
    {accessibleLabel && <span className="sr-only">{accessibleLabel}</span>}
  </span>
);

/** Green/red dot for a single store in the pickup-point dropdown (10px, rendered before the name). */
const storeStatusDot = (
  store: { coloradepotid: string },
  storeStockMap: Map<string, 'green' | 'red'>,
  statusLabels: { green: string; red: string },
) => {
  const status = storeStockMap.get(store.coloradepotid);

  if (!status) {
    return null;
  }

  return (
    <span className="relative inline-flex h-2.5 w-2.5 shrink-0">
      <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-full ${statusColorClasses[status]}`} />
      <span className="sr-only">{statusLabels[status]}</span>
    </span>
  );
};

/** The active pickup-point dropdown shown when Pickup is selected. */
const activeAfhaalpuntField = (
  favouriteShopId: string | undefined,
  selectedShop: ShopSelection | undefined,
  stores: types.Store[],
  storeStockMap: Map<string, 'green' | 'red'>,
  handleShopChange: (shop: ShopSelection) => void,
  afhaalpuntLabel: string,
  favouriteLabel: string,
  statusLabels: { green: string; red: string },
  b2b: boolean,
  distanceLocale: string,
) => (
  <PickupPointDropdown
    distanceLocale={distanceLocale}
    earlyGeolocation
    favouriteLabel={favouriteLabel}
    favouriteShopId={favouriteShopId}
    onChange={shop => handleShopChange({ id: shop.id, coloradepotid: shop.coloradepotid, name: shop.name })}
    renderStoreStatus={store => storeStatusDot(store, storeStockMap, statusLabels)}
    searchLabel={afhaalpuntLabel}
    selectLabel={afhaalpuntLabel}
    stores={stores ?? []}
    value={selectedShop?.id}
    variant={b2b ? 'basket' : 'basketB2c'}
  />
);

/** The greyed-out pickup-point display kept visible when Delivery is selected (last choice retained). */
const greyedAfhaalpuntField = (
  selectedShop: ShopSelection | undefined,
  favouriteShopId: string | undefined,
  storeStockMap: Map<string, 'green' | 'red'>,
  afhaalpuntLabel: string,
  favouriteLabel: string,
  statusLabels: { green: string; red: string },
  b2b: boolean,
) => (
  <div
    aria-disabled="true"
    className="flex h-10 w-full min-w-0 max-w-[320px] items-center justify-between rounded-[6px] border border-gray-200 bg-white px-3 opacity-50"
  >
    <span className="flex min-w-0 items-center gap-2">
      {selectedShop && storeStatusDot(selectedShop, storeStockMap, statusLabels)}
      <span className="text-gray-dark min-w-0 truncate text-base font-bold">
        {selectedShop?.name ?? afhaalpuntLabel}
      </span>
      {selectedShop?.id === favouriteShopId && (
        <span className="text-orderImport-step-description-text shrink-0 text-xs font-normal">· {favouriteLabel}</span>
      )}
    </span>
    <FontAwesomeIcon icon={b2b ? faAngleDown : faChevronDown} size="sm" />
  </div>
);

/** The pickup-point dropdown (active) or greyed-out display (when Delivery is selected). */
const afhaalpuntField = (
  isPickup: boolean,
  favouriteShopId: string | undefined,
  selectedShop: ShopSelection | undefined,
  stores: types.Store[],
  storeStockMap: Map<string, 'green' | 'red'>,
  handleShopChange: (shop: ShopSelection) => void,
  afhaalpuntLabel: string,
  favouriteLabel: string,
  statusLabels: { green: string; red: string },
  b2b: boolean,
  distanceLocale: string,
) =>
  isPickup
    ? activeAfhaalpuntField(
        favouriteShopId,
        selectedShop,
        stores,
        storeStockMap,
        handleShopChange,
        afhaalpuntLabel,
        favouriteLabel,
        statusLabels,
        b2b,
        distanceLocale,
      )
    : greyedAfhaalpuntField(
        selectedShop,
        favouriteShopId,
        storeStockMap,
        afhaalpuntLabel,
        favouriteLabel,
        statusLabels,
        b2b,
      );

const methodLabelClasses = {
  selected: 'text-green-dark font-bold',
  unselected: 'text-gray-dark font-normal',
} as const;

const summaryTextColorClasses = {
  green: 'text-green',
  red: 'text-red',
} as const;

const getOptionIds = (method: DeliveryMethod) => {
  const id = method === 'Pickup' ? 'basket-pickup' : 'basket-delivery';

  return { id, labelId: `${id}-label` };
};

const getOptionLabelClass = (isSelected: boolean): string =>
  isSelected ? methodLabelClasses.selected : methodLabelClasses.unselected;

const getCostClassName = (method: DeliveryMethod): string =>
  method === 'Pickup' ? 'text-green-dark' : 'text-gray-dark';

// [46232] Stock label is selection-independent: each option shows its own known
// status (green and red) whether or not it is the selected method.
const getVisibleStatusLabel = (summary: StockStatusSummary, statusLabel?: string): string | undefined =>
  summary ? statusLabel : undefined;

const getStatusDotLabel = (
  summary: StockStatusSummary,
  visibleStatusLabel: string | undefined,
  statusLabels: { green: string; red: string },
): string | undefined => (visibleStatusLabel || !summary ? undefined : statusLabels[summary]);

const methodOption = (
  method: DeliveryMethod,
  selectedMethod: DeliveryMethod,
  label: string,
  onChange: (method: DeliveryMethod) => void,
  summary: StockStatusSummary,
  statusLabels: { green: string; red: string },
  statusLabel?: string,
  cost?: string | null,
  message?: string | null,
) => {
  const isSelected = method === selectedMethod;
  const { id, labelId } = getOptionIds(method);
  const visibleStatusLabel = getVisibleStatusLabel(summary, statusLabel);

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
        <input
          aria-labelledby={labelId}
          checked={isSelected}
          className="border-1 border-gray checked:border-5 checked:border-green focus-visible:ring-green h-4 w-4 shrink-0 appearance-none rounded-full border-solid !bg-white focus-visible:ring-2"
          id={id}
          name="levermethode-basket"
          onChange={() => onChange(method)}
          type="radio"
          value={method}
        />
        <label
          className={`min-w-0 cursor-pointer text-sm ${getOptionLabelClass(isSelected)}`}
          htmlFor={id}
          id={labelId}
        >
          {label}
        </label>
        {summary && summaryDot(summary, true, getStatusDotLabel(summary, visibleStatusLabel, statusLabels))}
        {summary && visibleStatusLabel && (
          <span className={`text-xs font-semibold ${summaryTextColorClasses[summary]}`}>{visibleStatusLabel}</span>
        )}
        {cost && <span className={`ml-auto shrink-0 text-[13px] font-bold ${getCostClassName(method)}`}>{cost}</span>}
      </div>
      {message && <p className="text-gray-dark m-0 ml-[26px] text-xs">{message}</p>}
    </div>
  );
};

const b2bChoice = (
  title: string,
  deliveryLabel: string,
  pickupLabel: string,
  deliveryMethod: DeliveryMethod,
  handleDeliveryMethodChange: (method: DeliveryMethod) => void,
  methodSummaries: { Delivery: StockStatusSummary; Pickup: StockStatusSummary },
  statusLabels: { green: string; red: string },
  pickupField: React.ReactNode,
) => {
  const deliveryStatusLabel = methodSummaries.Delivery ? statusLabels[methodSummaries.Delivery] : undefined;
  const pickupStatusLabel = methodSummaries.Pickup ? statusLabels[methodSummaries.Pickup] : undefined;

  return (
    <section className="rounded-2 pt-4.5 bg-basket-delivery-panel border-1 mb-5 flex flex-col gap-1.5 border-gray-200 px-5 pb-[39px]">
      <h2 className="!text-gray-dark m-0 text-sm font-bold !leading-[20px]" id="basket-delivery-method-title">
        {title}
      </h2>
      <div aria-labelledby="basket-delivery-method-title" className="flex min-w-0 flex-col gap-3.5" role="radiogroup">
        {methodOption(
          'Delivery',
          deliveryMethod,
          deliveryLabel,
          handleDeliveryMethodChange,
          methodSummaries.Delivery,
          statusLabels,
          deliveryStatusLabel,
        )}
        <div className="flex min-w-0 flex-col gap-3">
          {methodOption(
            'Pickup',
            deliveryMethod,
            pickupLabel,
            handleDeliveryMethodChange,
            methodSummaries.Pickup,
            statusLabels,
            pickupStatusLabel,
          )}
          <div className="ml-[26px] min-w-0">{pickupField}</div>
        </div>
      </div>
    </section>
  );
};

// --- Component ---

/**
 * [46232] sv05 basket delivery-method choice — presentational only.
 * Owns no state, emits changes via callbacks.
 * B2B: Delivery-first binary radios + pickup-point dropdown.
 * B2C: compact radio rows with the pickup selector inline.
 * Shows a method-specific status dot when that method's stock summary is known.
 * Greyed-out pickup-point field when Delivery is selected (last choice retained).
 */
const BasketDeliveryMethodChoice = ({
  deliveryMethod,
  favouriteShopId,
  onDeliveryMethodChange: handleDeliveryMethodChange,
  onShopChange: handleShopChange,
  selectedShop,
  stores,
  storeStockMap,
  methodSummaries,
}: Props) => {
  const { t, i18n } = useTranslation(['product', 'forms', 'checkout']);

  const isPickup = deliveryMethod === 'Pickup';
  const pickupLabel = t('productPanel.shopDelivery.title', { defaultValue: 'Afhaling in de winkel' });
  const afhaalpuntLabel = t('productPanel.levermethode.chooseAfhaalpunt', { defaultValue: 'Kies je afhaalpunt' });
  const favouriteLabel = t('productPanel.levermethode.favourite', { defaultValue: 'favoriet' });
  const availableLabel = t('productPanel.stock.availability.available', { defaultValue: 'Op voorraad' });
  const insufficientLabel = t('productPanel.stock.availability.insufficient', { defaultValue: 'Onvoldoende' });
  const statusLabels = { green: availableLabel, red: insufficientLabel };
  const deliveryMethodTitle = t('forms:deliverymethod', { defaultValue: 'Levermethode' });
  const b2bDeliveryLabel = t('checkout:deliveryOptions.delivery.label', { defaultValue: 'Leveren' });
  const b2bPickupLabel = t('checkout:deliveryOptions.pickup.label', { defaultValue: 'Afhalen' });
  const afhaalpuntFieldNode = afhaalpuntField(
    isPickup,
    favouriteShopId,
    selectedShop,
    stores,
    storeStockMap,
    handleShopChange,
    afhaalpuntLabel,
    favouriteLabel,
    statusLabels,
    isB2b,
    i18n.language,
  );

  if (isB2b) {
    return b2bChoice(
      deliveryMethodTitle,
      b2bDeliveryLabel,
      b2bPickupLabel,
      deliveryMethod,
      handleDeliveryMethodChange,
      methodSummaries,
      statusLabels,
      afhaalpuntFieldNode,
    );
  }

  const pickupSummary = methodSummaries.Pickup;
  // [46232] Show the pickup status label for any known summary (green OR red) — a
  // low-stock store must read "Onvoldoende", not disappear. Selection-independent.
  const pickupStatusLabel = pickupSummary ? statusLabels[pickupSummary] : undefined;
  const pickupCost = t('checkout:deliveryOptions.pickup.free');

  return (
    <section className="rounded-2 pt-4.5 bg-basket-delivery-panel border-1 mb-5 flex flex-col gap-1.5 border-gray-200 px-5 pb-[19px]">
      <h2 className="!text-gray-dark m-0 text-sm font-bold !leading-[20px]" id="basket-delivery-method-title">
        {deliveryMethodTitle}
      </h2>
      <div aria-labelledby="basket-delivery-method-title" className="flex min-w-0 flex-col gap-3" role="radiogroup">
        <div className="flex flex-col gap-1">
          {methodOption(
            'Pickup',
            deliveryMethod,
            pickupLabel,
            handleDeliveryMethodChange,
            pickupSummary,
            statusLabels,
            pickupStatusLabel ?? undefined,
            pickupCost,
            t('checkout:deliveryOptions.pickup.title'),
          )}
          <div className="ml-[26px] min-w-0">{afhaalpuntFieldNode}</div>
        </div>
        {methodOption(
          'Delivery',
          deliveryMethod,
          t('productPanel.levermethode.delivery', { defaultValue: 'Levering aan huis' }),
          handleDeliveryMethodChange,
          methodSummaries.Delivery,
          statusLabels,
          undefined,
          t('checkout:deliveryOptions.delivery.subtext'),
          t('checkout:deliveryOptions.delivery.title'),
        )}
      </div>
    </section>
  );
};

export default BasketDeliveryMethodChoice;
